import React, { useState, useEffect } from 'react';
import { Download, FileText, FileCode, Loader2, CheckCircle, XCircle, X } from 'lucide-react';
import { downloadService } from '../../services/downloadService';

type EditorBlock = {
  id: string;
  type: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  content: any;
  style: any;
  zIndex: number;
  visible?: boolean;
};

type EditorPage = {
  id: string;
  name: string;
  blocks: EditorBlock[];
  canvasSize: { width: number; height: number };
  background?: any;
};

type EditorState = {
  pages: EditorPage[];
  currentPageId: string;
  selectedBlockId: string | null;
  zoom: number;
  showGrid: boolean;
};

type ExportDialogProps = {
  editorState: EditorState;
  isOpen: boolean;
  onClose: () => void;
  templateName?: string;
};

type ExportStatus = 'idle' | 'loading' | 'success' | 'error';
type ExportType = 'pdf' | 'word' | 'html';

type ExportState = {
  status: ExportStatus;
  message?: string;
};

const initialExportState: ExportState = { status: 'idle' };

export const ExportDialog = ({ editorState, isOpen, onClose, templateName }: ExportDialogProps) => {
  const [exportStates, setExportStates] = useState<Record<ExportType, ExportState>>({
    pdf: initialExportState,
    word: initialExportState,
    html: initialExportState,
  });

  // Reset all export states when the dialog opens fresh
  useEffect(() => {
    if (isOpen) {
      setExportStates({ pdf: initialExportState, word: initialExportState, html: initialExportState });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const baseName = templateName || 'template';

  const setExportStatus = (type: ExportType, state: ExportState) => {
    setExportStates((prev) => ({ ...prev, [type]: state }));
  };

  const handleExport = async (type: ExportType) => {
    // Prevent concurrent exports of same type
    if (exportStates[type].status === 'loading') return;

    setExportStatus(type, { status: 'loading' });
    try {
      switch (type) {
        case 'pdf':
          await downloadService.downloadAsPDF(editorState, `${baseName}.pdf`);
          break;
        case 'word':
          await downloadService.downloadAsWord(editorState, `${baseName}.docx`);
          break;
        case 'html':
          await downloadService.downloadAsHTML(editorState, `${baseName}.html`);
          break;
      }
      setExportStatus(type, { status: 'success' });
      // Reset to idle after 3 seconds
      setTimeout(() => setExportStatus(type, initialExportState), 3000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed';
      setExportStatus(type, { status: 'error', message });
      setTimeout(() => setExportStatus(type, initialExportState), 5000);
    }
  };

  const isAnyLoading = Object.values(exportStates).some((s) => s.status === 'loading');

  const exportOptions: Array<{
    type: ExportType;
    icon: React.ReactNode;
    label: string;
    description: string;
    accentColor: string;
    bgColor: string;
  }> = [
    {
      type: 'pdf',
      icon: <Download className="w-5 h-5" />,
      label: 'Download as PDF',
      description: 'Pixel-perfect PDF — exact match to your preview',
      accentColor: '#ef4444',
      bgColor: '#fef2f2',
    },
    {
      type: 'word',
      icon: <FileText className="w-5 h-5" />,
      label: 'Download as Word',
      description: 'Microsoft Word (.docx) — editable document',
      accentColor: '#2563eb',
      bgColor: '#eff6ff',
    },
    {
      type: 'html',
      icon: <FileCode className="w-5 h-5" />,
      label: 'Download as HTML',
      description: 'Self-contained HTML file — opens in any browser',
      accentColor: '#059669',
      bgColor: '#ecfdf5',
    },
  ];

  return (
    // Overlay
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Modal */}
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        style={{ border: '1px solid #e5e7eb' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Download Document</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {editorState.pages.length} page{editorState.pages.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isAnyLoading}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-40"
            title="Close"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Export Options */}
        <div className="p-4 space-y-3">
          {exportOptions.map(({ type, icon, label, description, accentColor, bgColor }) => {
            const state = exportStates[type];
            const isLoading = state.status === 'loading';
            const isSuccess = state.status === 'success';
            const isError = state.status === 'error';

            return (
              <button
                key={type}
                onClick={() => handleExport(type)}
                disabled={isLoading || isAnyLoading}
                className="w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-150 text-left group"
                style={{
                  backgroundColor: isLoading || isSuccess || isError ? bgColor : '#f9fafb',
                  border: `1.5px solid ${isLoading || isSuccess ? accentColor : isError ? '#ef4444' : '#e5e7eb'}`,
                  opacity: isAnyLoading && !isLoading ? 0.5 : 1,
                  cursor: isAnyLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {/* Icon */}
                <div
                  className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: bgColor, color: accentColor }}
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isSuccess ? (
                    <CheckCircle className="w-5 h-5" style={{ color: accentColor }} />
                  ) : isError ? (
                    <XCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    icon
                  )}
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {isLoading ? `Downloading ${type.toUpperCase()}...`
                     : isSuccess ? `${type.toUpperCase()} Downloaded!`
                     : isError ? `${type.toUpperCase()} Failed`
                     : label}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 truncate">
                    {isError ? (state.message || 'Please try again.') : description}
                  </div>
                </div>

                {/* Arrow (idle only) */}
                {state.status === 'idle' && (
                  <div className="flex-shrink-0 text-gray-300 group-hover:text-gray-500 transition-colors">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="px-6 pb-4">
          <p className="text-xs text-gray-400 text-center">
            PDF uses server-side rendering for pixel-perfect output
          </p>
        </div>
      </div>
    </div>
  );
};
